import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import { isEmpty } from 'lodash';
import moment from 'moment';

import { getTravelsRequested, TravelRequestPayload } from 'actions/travelRequested';
import AppBar from 'components/AppBar';
import Pagination from 'components/Pagination';
import { Desktop, Mobile } from 'components/Responsive';
import ScreenHeader from 'components/ScreenHeader';
import Shimmer from 'components/Shimmer';
import { SearchParams, SearchTypes } from 'components/TripSearch/models';
import TripSearchContainer from 'containers/TripSearch';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import useUserAccount from 'hooks/useUserAccount';
import { TravelRequested as TravelRequestedModel } from 'models/travelRequested';
import EmptyView from 'screens/components/TravelRequested/components/EmptyView';
import FilterTags from 'screens/components/TravelRequested/components/FilterTags';
import List from 'screens/components/TravelRequested/components/List';
import Tab from 'screens/components/TravelRequested/components/Tab';
import { amplitudelogEvent, ViewTravelReservationEvent } from 'services/amplitudeService';
import { amplitudeEvents } from 'utils/amplitudeEvents';
import { checkFilterTagsMapper, CheckBy } from 'utils/filterTags';
import { extractErrorMessage } from 'utils/generic';

import {
  Container,
  Content,
  TabContainer,
  Subtitle,
  HeaderContainer,
  ButtonsContainer,
  SearchButton,
  IconSearch,
} from './styles';

const TravelRequested = () => {
  const { t } = useStoreTranslation();
  const [active, setActive] = useState(SearchTypes.next);

  const [travels, setTravels] = useState<TravelRequestedModel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const MAX_ITEMS_PER_PAGE = 10;
  const [showSearch, setShowSearch] = useState(false);
  const [searchParams, setSearchParams] = useState<SearchParams>({ type: SearchTypes.next, page: 1 });
  const DEFAULT_INIT_DATE = undefined;
  const DEFAULT_END_DATE = undefined;

  const toast = useToast();
  const history = useHistory();
  const user = useUserAccount();

  const handleGoBack = () => {
    if (user?.isGroupApprover) {
      history.push('/dashboard');
    } else {
      history.push('/booking');
    }
  };

  const updateParams = (newParams: SearchParams) => {
    setSearchParams(newParams);
    setCurrentPage(newParams.page ?? 1);
    setActive((newParams.type as SearchTypes) ?? (SearchTypes.next as SearchTypes));
    setLoaded(false);
  };

  const handlePageChange = useCallback(
    (p: number) => {
      const params = { ...searchParams, page: p };
      updateParams(params);
    },
    [searchParams],
  );

  const getDates = (active) => {
    const initDate =
      active === SearchTypes.next
        ? `${moment().format('YYYY-MM-DD')}T00:00:00Z`
        : active === SearchTypes.prev
        ? `${moment().subtract(6, 'months').format('YYYY-MM-DD')}T00:00:00Z`
        : DEFAULT_INIT_DATE;
    const endDate =
      active === SearchTypes.next
        ? `${moment().add(6, 'months').format('YYYY-MM-DD')}T00:00:00Z`
        : active === SearchTypes.prev
        ? `${moment().format('YYYY-MM-DD')}T00:00:00Z`
        : DEFAULT_END_DATE;
    return {
      initDate,
      endDate,
    };
  };

  const fetchTravels = async () => {
    if (!user) return;
    try {
      setLoaded(false);
      setIsLoading(true);
      setTravels([]);
      setTotal(0);
      window.scrollTo(0, 0);
      const { initDate, endDate } = getDates(active);
      const payload: TravelRequestPayload = {
        userId: user?.id,
        initDate,
        endDate,
        maxItemsPerPage: MAX_ITEMS_PER_PAGE,
      };
      const params = { ...searchParams };
      setCurrentPage(params.page ?? 1);
      payload.page = params.page ?? 1;
      if (!isEmpty(params)) {
        if (params.initDate && params.endDate) {
          payload.initDate = params.initDate;
          payload.endDate = params.endDate;
        } else {
          payload.initDate = initDate;
          payload.endDate = endDate;
          setSearchParams({ ...params, initDate, endDate });
        }
        if (params.distanceRestriction) payload.distanceRestriction = params.distanceRestriction;
        if (params.tripType) payload.travelType = params.tripType;
        if (params.destination) {
          payload.destinationCode = params.destination.code;
          payload.destinationType = 'city';
        }
      } else {
        setSearchParams({ initDate, endDate });
      }

      const res = await getTravelsRequested(payload);

      if (res.data.data) {
        setTravels(res.data.data.travels);
        setTotal(res.data.data.total);
      }
    } catch (error) {
      toast.add({
        type: 'danger',
        message: extractErrorMessage(error) || t('defaultError.default'),
      });
    } finally {
      setLoaded(true);
      setIsLoading(false);
    }

    amplitudelogEvent(
      amplitudeEvents.VIEW_TRAVEL_RESERVATION,
      {
        SOURCE: 'MIS_VIAJES',
      } as ViewTravelReservationEvent,
      user,
    );
  };

  useEffect(() => {
    if (!loaded && !isLoading) fetchTravels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, active, loaded, isLoading, searchParams]);

  const tabs = [
    {
      key: SearchTypes.next,
      label: t('myTrips.travels.next'),
      testId: 'my-travels-upcoming-button',
    },
    {
      key: SearchTypes.prev,
      label: t('myTrips.travels.previous'),
      testId: 'my-travels-previous-button',
    },
  ];

  const LoadingButtons = () => (
    <ButtonsContainer>
      <TabContainer>
        {Number(2)
          .toArray()
          .map((i) => (
            <Tab
              key={i}
              label={<Shimmer>Option</Shimmer>}
              isActive={false}
              onClick={() => {}}
              testId="test-id-loading"
            />
          ))}
      </TabContainer>
    </ButtonsContainer>
  );

  const checkFilterTags = useCallback(
    (searchParams?: SearchParams) =>
      checkFilterTagsMapper(DEFAULT_INIT_DATE, DEFAULT_END_DATE, CheckBy.SearchTypes, searchParams),
    [DEFAULT_INIT_DATE, DEFAULT_END_DATE],
  );

  const hasTags = useMemo(
    () => loaded && searchParams.type !== SearchTypes.next && searchParams.type !== SearchTypes.prev,
    [loaded, searchParams.type],
  );

  return (
    <Container>
      <Mobile>
        <AppBar goBack={handleGoBack}>
          <HeaderContainer>
            <h5>{t('myTrips.title')}</h5>
            <Subtitle>{t('myTrips.detail')}</Subtitle>
          </HeaderContainer>
        </AppBar>
      </Mobile>
      <Desktop>
        <ScreenHeader title={t('myTrips.title')} subtitle={t('myTrips.detail')} />
      </Desktop>
      <Content>
        {isLoading
          ? LoadingButtons()
          : loaded && (
              <ButtonsContainer>
                <TabContainer>
                  {tabs.map((i) => (
                    <Tab
                      key={i.key}
                      onClick={() => {
                        updateParams({ page: 1, type: i.key });
                      }}
                      isActive={active === i.key}
                      label={i.label}
                      testId={i.testId}
                    />
                  ))}
                </TabContainer>
                <SearchButton onClick={() => setShowSearch(true)}>
                  <IconSearch />
                </SearchButton>
              </ButtonsContainer>
            )}
        {!isLoading && hasTags && (
          <FilterTags
            params={searchParams ?? {}}
            onChange={(params) => {
              const newParams = checkFilterTags(params)
                ? { ...params, page: 1, type: SearchTypes.search }
                : { page: 1, type: SearchTypes.next };
              updateParams(newParams);
            }}
          />
        )}
        {!isLoading && loaded && travels.length === 0 && (
          <EmptyView key="travel" description={t('myTrips.travels.emptyMessage')} />
        )}
        <>
          <List
            isLoading={isLoading}
            type="travels"
            data={travels}
            hasFilterTags={checkFilterTags(searchParams)}
            fetchTravels={fetchTravels}
          />
          {loaded && (
            <Pagination
              currentPage={currentPage}
              maxItemsPerPage={MAX_ITEMS_PER_PAGE}
              total={total}
              onClick={handlePageChange}
            />
          )}
          {loaded && (
            <TripSearchContainer
              show={showSearch}
              onClose={() => setShowSearch(false)}
              onSearch={(params: SearchParams) => {
                setShowSearch(false);
                const newParams = { ...params, page: 1, type: SearchTypes.search };
                updateParams(newParams);
              }}
              params={searchParams}
            />
          )}
        </>
      </Content>
    </Container>
  );
};

export default TravelRequested;
